import React, { useState, useMemo } from "react";

const EventListings = ({
  events = [],
  loading,
  stats,
  setActiveTab,
  handleViewClick,
  handleEditClick,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date"); // title, date, status
  const [sortOrder, setSortOrder] = useState("desc");

  const sortedAndFilteredEvents = useMemo(() => {
    let filtered = events.filter((event) => {
      const title = (event.title || event.name || "").toLowerCase();
      const category = (event.category || event.type || "").toLowerCase();
      const location = (event.venue || event.locationType || "").toLowerCase();
      const status = (event.status || "pending").toLowerCase();
      
      const matchesSearch = 
        title.includes(searchTerm.toLowerCase()) ||
        category.includes(searchTerm.toLowerCase()) ||
        location.includes(searchTerm.toLowerCase());
      
      const matchesCategory = categoryFilter === "all" || category.includes(categoryFilter.toLowerCase());
      const matchesStatus = statusFilter === "all" || status === statusFilter.toLowerCase();
      
      return matchesSearch && matchesCategory && matchesStatus;
    });

    return filtered.sort((a, b) => {
      let comparison = 0;
      if (sortBy === "title") {
        comparison = (a.title || a.name || "").localeCompare(b.title || b.name || "");
      } else if (sortBy === "date") {
        comparison = new Date(a.startDate || 0) - new Date(b.startDate || 0);
      } else if (sortBy === "status") {
        comparison = (a.status || "pending").localeCompare(b.status || "pending");
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });
  }, [events, searchTerm, categoryFilter, statusFilter, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  // Helper to get a consistent color based on event title
  const getAvatarColor = (title) => {
    const colors = [
      "bg-primary",
      "bg-success",
      "bg-info",
      "bg-warning",
      "bg-danger",
      "bg-secondary",
    ];
    let hash = 0;
    for (let i = 0; i < title.length; i++) {
      hash = title.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div className="event-listings-section mt-4">
      {/* Statistics Cards */}
      <div className="row g-4">
        <div className="col-12 col-md-4">
          <div className="event-stat-card">
            <div className="card-top">
              <div>
                <p className="card-label">Total Events</p>
                <h2 className="card-value">{stats.totalEvents}</h2>
              </div>
              <div className="card-icon-wrapper icon-bg-blue">
                <i className="fa-solid fa-calendar-check icon-text-blue" />
              </div>
            </div>
            <div className="card-footer">
              <p className="description">from last month</p>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="event-stat-card">
            <div className="card-top">
              <div>
                <p className="card-label">Upcoming</p>
                <h2 className="card-value">{stats.upcomingEvents}</h2>
              </div>
              <div className="card-icon-wrapper icon-bg-orange">
                <i className="fa-solid fa-clock icon-text-orange" />
              </div>
            </div>
            <div className="card-footer">
              <p className="description">Scheduled soon</p>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="event-stat-card">
            <div className="card-top">
              <div>
                <p className="card-label">Total Revenue</p>
                <h2 className="card-value">${stats.totalRevenue}</h2>
              </div>
              <div className="card-icon-wrapper icon-bg-green">
                <i className="fa-solid fa-wallet icon-text-green" />
              </div>
            </div>
            <div className="card-footer">
              <p className="description">Sales</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Table Card */}
      <div className="modern-card mt-5">
        {/* Filters Bar */}
        <div className="modern-filter-bar">
          <div className="d-flex flex-column flex-md-row gap-4">
            {/* Search */}
            <div className="modern-search-wrapper w-100">
              <i className="isax isax-search-normal-1" />
              <input
                type="text"
                className="modern-search-input"
                placeholder="Search events by title, category, location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="d-flex flex-wrap align-items-center gap-3">
              {/* Category Filter */}
              <div className="modern-select-wrapper">
                <i className="isax isax-category prefix-icon" />
                <select 
                  className="modern-select"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  <option value="music">Music</option>
                  <option value="tech">Technology</option>
                  <option value="sports">Sports</option>
                </select>
                <i className="isax isax-arrow-down-1 suffix-icon" />
              </div>

              {/* Status Filter */}
              <div className="modern-select-wrapper">
                <i className="isax isax-filter prefix-icon" />
                <select 
                  className="modern-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="completed">Completed</option>
                </select>
                <i className="isax isax-arrow-down-1 suffix-icon" />
              </div>

              {/* Results count */}
              <div className="results-badge">
                {loading ? (
                  <span>Loading...</span>
                ) : (
                  <>
                    <span className="fw-bold text-dark me-1">
                      {sortedAndFilteredEvents.length}
                    </span>
                    <span>events</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Table Header */}
        <div className="modern-table-header flex-column flex-md-row align-items-start align-items-md-center">
          <h2 className="fw-bold mb-3 mb-md-0">Event Directory ({sortedAndFilteredEvents.length})</h2>
          <div className="d-flex flex-wrap align-items-center gap-3 gap-md-4">
            <span className="text-muted small">Sort by:</span>
            <div className="d-flex gap-2">
              <button 
                className={`sort-btn ${sortBy === 'title' ? 'active' : ''}`}
                onClick={() => handleSort('title')}
              >
                Title {sortBy === 'title' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
              <button 
                className={`sort-btn ${sortBy === 'date' ? 'active' : ''}`}
                onClick={() => handleSort('date')}
              >
                Date {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
              <button 
                className={`sort-btn ${sortBy === 'status' ? 'active' : ''}`}
                onClick={() => handleSort('status')}
              >
                Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
            </div>
            <button
              className="btn btn-primary-gradient btn-sm rounded-pill px-4"
              onClick={() => setActiveTab("create")}
            >
              <i className="isax isax-add-circle me-1"></i> New Event
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-5 text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted fw-medium">Loading events...</p>
          </div>
        ) : sortedAndFilteredEvents.length > 0 ? (
          <div className="table-responsive">
            <table className="table modern-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Schedule</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedAndFilteredEvents.map((event) => (
                  <tr key={event._id || event.id}>
                    {/* Event Info */}
                    <td>
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className={`event-avatar ${getAvatarColor(event.title || event.name || "E")} text-white`}
                        >
                          {(event.title || event.name || "E").charAt(0)}
                        </div>
                        <div>
                          <p className="fw-bold text-dark mb-0">
                            {event.title || event.name}
                          </p>
                          <p className="text-muted small mb-0">
                            {event.category || event.type || "General"}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Schedule */}
                    <td>
                      <div className="d-flex flex-column">
                        <span className="fw-medium text-dark">
                          {event.startDate
                            ? new Date(event.startDate).toLocaleDateString()
                            : "N/A"}
                        </span>
                        <span className="text-muted small">
                          {event.startDate
                            ? new Date(event.startDate).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : ""}
                        </span>
                      </div>
                    </td>

                    {/* Location */}
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <i className="isax isax-location text-danger small" />
                        <span className="text-dark small fw-medium">
                          {event.venue || event.locationType || "TBA"}
                        </span>
                      </div>
                    </td>

                    {/* Status */}
                    <td>
                      <span
                        className={`status-badge ${(event.status || "pending").toLowerCase()}`}
                      >
                        {event.status || "Pending"}
                      </span>
                    </td>

                    {/* Actions */}
                    <td>
                      <div className="d-flex justify-content-end gap-1">
                        <button
                          className="modern-action-btn view"
                          title="View Event"
                          onClick={() => handleViewClick(event)}
                        >
                          <i className="isax isax-eye"></i>
                        </button>
                        <button
                          className="modern-action-btn edit"
                          title="Edit Event"
                          onClick={() => handleEditClick(event)}
                        >
                          <i className="isax isax-edit"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-5 text-center bg-light bg-opacity-25">
            <div
              className="mx-auto bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mb-4"
              style={{ width: "80px", height: "80px" }}
            >
              <i
                className="isax isax-calendar-remove text-primary"
                style={{ fontSize: "40px" }}
              ></i>
            </div>
            <h3 className="fw-bold text-dark mb-2">No events found</h3>
            <p className="text-muted mb-4 mx-auto" style={{ maxWidth: "400px" }}>
              Start by creating your first event to build your directory!
            </p>
            <button
              className="btn btn-primary-gradient rounded-pill px-5"
              onClick={() => setActiveTab("create")}
            >
              Create First Event
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventListings;
